import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { LoginResponse, SeleccionarEntidadResponse } from '@/modules/login/types/interfaces/AutenticacionV2';
import {
  Logout,
  RefreshToken as RefreshTokenService,
  SeleccionarEntidad as SeleccionarEntidadService,
  SeleccionarPersonaFisica as SeleccionarPersonaFisicaService
} from '@/modules/login/services/GestionCasosApi/AutenticacionV2.service';
import { isTokenExpired as isStoredTokenExpired } from '../services/Http.service';
import { storage } from '../utils/Const.utils';
import { AUTH_SYNC_CHANNEL, TAB_ID } from '../utils/authSync.utils';
import { CLAIM_ID_REL_USUARIO_ENTIDADES, obtenerClaimToken, tokenTieneEntidad } from '../utils/jwt.utils';
import { StorageKeys } from '../types/enums/Storage.enum';
import type { TipoUsuarioPerfilOpcionEnum } from '../../modules/escritorio/types/enums/TipoUsuario.enum';
import type { Sexo } from '../../modules/escritorio/types/enums/Genero.enum';
import type { DeviceInfo } from '@/modules/security/types/interfaces/DeviceSession';
import { DeviceBlocked } from '../../modules/security/components/DeviceBlocked';
import { customAlert } from '../components';

export interface SystemTime {
  fechaActual: Date;
  fechaActualUtc: Date;
}

/**
 * Interfaz para el usuario autenticado
 */
export interface AuthUser {
  id: string;
  idPerfil: TipoUsuarioPerfilOpcionEnum;
  fullName?: string;
  email?: string;
  imageUrl?: string;
  matricula?: string;
  genero?: string;
  idSexo?: Sexo;
  identificacion?: string;
  telefono?: string;
  fechaEmisionCarnet?: string;
  celular?: string;
  rnc?: string;
  esPrimerInicioSesion: boolean;
  esActivo2FA: boolean;
  systemTime: SystemTime;
  permisos: Array<{
    IdPermisoPerfil: number;
    IdPerfil: number;
    Perfil: string;
    IdModulo: number;
    Modulo: string;
    IdPermiso: number;
    Permiso: string;
    Activo: boolean;
    FechaCreacion: string;
    IdUsuarioCreacion: number;
    PcCreacion: string;
  }>;
}

/**
 * Estado del contexto de autenticación
 */
export interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  deviceSecurityBlock: {
    isBlocked: boolean;
    deviceInfo?: DeviceInfo | null;
  } | null;
  email: string;
}

/**
 * Acciones para el reducer de autenticación
 */
export type AuthAction =
  | { type: 'INIT_AUTH' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: AuthUser; tokens: LoginResponse['tokens'] } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH'; payload: { accessToken: string; expiresAt: string; refreshToken?: string } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'DEVICE_BLOCKED'; payload: { deviceInfo?: DeviceInfo | null, email?: string | null } }
  | { type: 'CLEAR_DEVICE_BLOCK' };

/**
 * Contexto de autenticación
 */
export interface AuthContextType {
  state: AuthState;
  login: (loginData: LoginResponse) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  /** Emitir tokens con la relación usuario-entidad elegida (reemplaza access y refresh token) */
  selectEntity: (idRelUsuarioEntidades: number) => Promise<void>;
  /** Emitir tokens sin entidad al pasar a Persona Física, solo si el token actual trae una. `true` si se emitieron */
  selectPersonaFisica: () => Promise<boolean>;
  isTokenExpired: () => boolean;
  hasPermission: (modulo: string, permiso?: string) => boolean;
  clearDeviceBlock: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Estado inicial del contexto
 */
const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  isLoading: true,
  isAuthenticated: false,
  deviceSecurityBlock: null,
  email: ''
};

/**
 * Reducer para manejar el estado de autenticación
 */
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INIT_AUTH':
      /* Inicializar desde localStorage */
      const accessToken = storage.get(StorageKeys.ACCESS_TOKEN);
      const refreshToken = storage.get(StorageKeys.REFRESH_TOKEN);
      const expiresAt = storage.get(StorageKeys.EXPIRES_AT);
      const userData = storage.get(StorageKeys.USER_DATA);

      if (accessToken && userData) {
        return {
          ...state,
          user: userData,
          accessToken,
          refreshToken,
          expiresAt,
          isLoading: false,
          isAuthenticated: true,
        };
      }

      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
      };

    case 'LOGIN_SUCCESS':
      const { user, tokens } = action.payload;
      
      /* Guardar en localStorage */
      storage.set(StorageKeys.ACCESS_TOKEN, tokens.AccessToken);
      storage.set(StorageKeys.REFRESH_TOKEN, tokens.RefreshToken);
      storage.set(StorageKeys.EXPIRES_AT, tokens.ExpiresAtUtc);
      storage.set(StorageKeys.USER_DATA, user);

      return {
        ...state,
        user,
        accessToken: tokens.AccessToken,
        refreshToken: tokens.RefreshToken,
        expiresAt: tokens.ExpiresAtUtc,
        isLoading: false,
        isAuthenticated: true,
      };

    case 'LOGIN_FAILURE':
      storage.clearAll();
      return {
        ...initialState,
        isLoading: false,
        isAuthenticated: false,
        deviceSecurityBlock: null,
      };

    case 'LOGOUT':
      /* Limpiar storage completamente en logout explícito (incluye PERFIL) */
      storage.clearAll();
      return {
        user: null,
        accessToken: null,
        refreshToken: null,
        expiresAt: null,
        isLoading: false, // <- Cambiar a false para que los componentes puedan redirigir
        isAuthenticated: false,
        deviceSecurityBlock: null,
        email: ''
      };

    case 'TOKEN_REFRESH':
      const { accessToken: newAccessToken, expiresAt: newExpiresAt, refreshToken: newRefreshToken } = action.payload;
      
      storage.set(StorageKeys.ACCESS_TOKEN, newAccessToken);
      storage.set(StorageKeys.EXPIRES_AT, newExpiresAt);
      
      // Actualizar refresh token si viene uno nuevo
      if (newRefreshToken) {
        storage.set(StorageKeys.REFRESH_TOKEN, newRefreshToken);
      }

      return {
        ...state,
        accessToken: newAccessToken,
        expiresAt: newExpiresAt,
        ...(newRefreshToken && { refreshToken: newRefreshToken }),
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'DEVICE_BLOCKED':
      storage.clearAll();
      return {
        ...initialState,
        isLoading: false,
        isAuthenticated: false,
        deviceSecurityBlock: {
          isBlocked: true,
          deviceInfo: action.payload.deviceInfo,
        },
        email: action.payload.email || ''
      };

    case 'CLEAR_DEVICE_BLOCK':
      return {
        ...state,
        deviceSecurityBlock: null,
      };

    default:
      return state;
  }
};

/**
 * Guardar los tokens en storage de inmediato. El reducer también los escribe, pero recién en el
 * siguiente render, y las operaciones encadenadas en la cola de rotación y Http.service leen del storage
 */
const guardarTokens = ({ accessToken, expiresAt, refreshToken }: { accessToken: string; expiresAt: string; refreshToken?: string }) => {
  storage.set(StorageKeys.ACCESS_TOKEN, accessToken);
  storage.set(StorageKeys.EXPIRES_AT, expiresAt);
  if (refreshToken) {
    storage.set(StorageKeys.REFRESH_TOKEN, refreshToken);
  }
};

/**
 * Props del proveedor de autenticación
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Proveedor del contexto de autenticación
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const lastActivityRef = useRef<number>(Date.now());
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasAuthenticatedRef = useRef<boolean>(false);
  
  /** Configuración de inactividad */
  const INACTIVITY_TIMEOUT = 20 * 60 * 1000; //<-- 20 minutos

  /**
   * Inicializar autenticación al cargar la aplicación
   */
  useEffect(() => {
    dispatch({ type: 'INIT_AUTH' });
  }, []);

  /**
   * Detectar actividad del usuario para prevenir logout durante uso activo
   */
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      
      // Reiniciar timeout de inactividad
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      
      activityTimeoutRef.current = setTimeout(() => {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
          console.log('Sesión cerrada por inactividad');
          customAlert({
            icon: 'warning',
            titulo: "<strong>Su sesión finalizará por inactividad</strong>",
            mensaje: `<div>
                        Por su seguridad, la sesión se cerrará debido a inactividad prolongada.
                        <br/><br/>
                        Si desea continuar en la sesión actual haga clic en el botón <span class="font-bold">Continuar sesión</span>.
                      </div>`,
            botonCancelar: true,
            textoBotonAceptar: 'Continuar sesión',
            textoBotonCancelar: 'Cerrar sesión',
            tiempo: 120, //<-- tiempo en segundos para autocierrre
            mensajeTiempoSWL: 'La sesión se cerrará automáticamente en',
            callback: (continuar) =>{
              if (!continuar) {
                dispatch({ type: 'LOGOUT' });
                customAlert({
                  icon: 'info',
                  titulo: '<strong>Sesión finalizada</strong>',
                  mensaje: 'Su sesión ha sido cerrada por inactividad.',
                });
              }
            }            
          });
        }
      }, INACTIVITY_TIMEOUT);
    };

    /*Eventos que indican actividad del usuario */
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Inicializar timeout
    updateActivity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [state.isAuthenticated, INACTIVITY_TIMEOUT]);

  /**
   * Sincronización entre pestañas con BroadcastChannel
   */
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);

    channel.onmessage = (event) => {
      const { type, payload, origen } = event.data;

      switch (type) {
        case 'TOKEN_REFRESHED':
          // Actualizar tokens en esta pestaña
          dispatch({
            type: 'TOKEN_REFRESH',
            payload
          });
          break;
        case 'LOGOUT':
          // Hacer logout en todas las pestañas
          dispatch({ type: 'LOGOUT' });
          break;
        case 'ENTITY_CHANGED':
          /* Otra pestaña cambió la entidad: recargar para tomar el nuevo perfil y token.
             El mensaje también llega a esta pestaña, por eso se ignora el propio */
          if (origen !== TAB_ID) {
            window.location.reload();
          }
          break;
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  /**
   * Notificar logout a otras pestañas (solo cuando hay un logout real, no al inicializar)
   */
  useEffect(() => {
    /* Solo notificar si cambió de autenticado a no autenticado (logout real) */
    if (wasAuthenticatedRef.current && !state.isAuthenticated && typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
      channel.postMessage({ type: 'LOGOUT' });
      channel.close();
    }
    
    /* Actualizar ref después de la inicialización */
    if (!state.isLoading) {
      wasAuthenticatedRef.current = state.isAuthenticated;
    }
  }, [state.isAuthenticated, state.isLoading]);

  /**
   * Cola de rotación de tokens: `refresh` y `seleccionar-entidad` revocan el refresh token anterior,
   * así que se ejecutan de a una dentro de la pestaña. La cola nunca queda rechazada
   */
  const rotacionEnCursoRef = useRef<Promise<void>>(Promise.resolve());
  const refreshEnCursoRef = useRef<Promise<void> | null>(null);

  const ejecutarRotacion = useCallback(<T,>(operacion: () => Promise<T>): Promise<T> => {
    const resultado = rotacionEnCursoRef.current.then(operacion);
    rotacionEnCursoRef.current = resultado.then(() => undefined, () => undefined);
    return resultado;
  }, []);

  /**
   * Función para refrescar el token con retry y backoff exponencial.
   * Usar solo dentro de la cola de rotación (ver `refreshToken` y `selectEntity`)
   */
  const refrescarTokenInterno = useCallback(async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 segundo
    
    try {
      const currentRefreshToken = storage.get(StorageKeys.REFRESH_TOKEN);
      
      if (!currentRefreshToken) {
        throw new Error('refresh token no disponible');
      }

      const response = await RefreshTokenService({ 
        refreshToken: currentRefreshToken,
        remoteIpAddress: ''
      });
      
      guardarTokens({
        accessToken: response.AccessToken,
        expiresAt: response.ExpiresAtUtc,
        refreshToken: response.RefreshToken
      });

      dispatch({
        type: 'TOKEN_REFRESH',
        payload: {
          accessToken: response.AccessToken,
          expiresAt: response.ExpiresAtUtc,
          refreshToken: response.RefreshToken
        }
      });
      
      // Notificar a otras pestañas
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
        channel.postMessage({
          type: 'TOKEN_REFRESHED',
          payload: {
            accessToken: response.AccessToken,
            expiresAt: response.ExpiresAtUtc,
            refreshToken: response.RefreshToken
          }
        });
        channel.close();
      }
    } catch (error) {
      console.error(`Error al refrescar el token (intento ${retryCount + 1}/${MAX_RETRIES}):`, error);
      
      // Retry con backoff exponencial
      if (retryCount < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        console.log(`Reintento en ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return refrescarTokenInterno(retryCount + 1);
      }
      
      // Después de todos los intentos, hacer logout
      console.error('Máximo número de reintentos alcanzado, cerrando sesión');
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  }, []);

  /**
   * Refrescar el token. Si ya hay un refresh en cola o en vuelo, se reutiliza esa misma promesa
   */
  const refreshToken = useCallback((): Promise<void> => {
    if (refreshEnCursoRef.current) return refreshEnCursoRef.current;

    const refresh = ejecutarRotacion(() => refrescarTokenInterno());
    refreshEnCursoRef.current = refresh;

    const liberar = () => {
      if (refreshEnCursoRef.current === refresh) refreshEnCursoRef.current = null;
    };
    refresh.then(liberar, liberar);

    return refresh;
  }, [ejecutarRotacion, refrescarTokenInterno]);

  /**
   * Reemplazar los tokens de la sesión por los que emite `seleccionar-entidad` o `seleccionar-persona-fisica`.
   * Ambos revocan el refresh token enviado. Usar solo dentro de la cola de rotación.
   * Si la llamada falla (403, red, etc.) no se modifica la sesión y el error se propaga tal cual
   */
  const reemplazarTokensSesion = useCallback(async (
    emitirTokens: (refreshToken: string) => Promise<SeleccionarEntidadResponse>,
    tokenCorresponde: (accessToken: string) => boolean,
    destino: string
  ): Promise<void> => {
    /* Evitar que la llamada salga con un access token vencido */
    if (isStoredTokenExpired()) {
      await refrescarTokenInterno();
    }

    /* Leer el refresh token justo antes de llamar: pudo rotar mientras esperaba en la cola */
    const currentRefreshToken = storage.get(StorageKeys.REFRESH_TOKEN);

    if (!currentRefreshToken) {
      throw new Error('refresh token no disponible');
    }

    const response = await emitirTokens(currentRefreshToken);

    /* El refresh token enviado ya quedó revocado: si la respuesta no es válida
       o pertenece a otro usuario, la sesión no puede continuar */
    const userData: AuthUser | null = storage.get(StorageKeys.USER_DATA);
    const esTextoValido = (valor: unknown): valor is string => typeof valor === 'string' && valor.trim() !== '';
    const esMismoUsuario = response?.IdUsuario == null || String(response.IdUsuario) === String(userData?.id);

    if (
      !esTextoValido(response?.AccessToken) ||
      !esTextoValido(response?.RefreshToken) ||
      !esTextoValido(response?.ExpiresAtUtc) ||
      !esMismoUsuario
    ) {
      console.error(`Respuesta inválida al seleccionar ${destino}, cerrando sesión`);
      await Logout({}).catch(() => undefined);
      dispatch({ type: 'LOGOUT' });
      throw new Error(`Respuesta inválida al seleccionar ${destino}`);
    }

    const tokens = {
      accessToken: response.AccessToken,
      expiresAt: response.ExpiresAtUtc,
      refreshToken: response.RefreshToken
    };

    guardarTokens(tokens);
    dispatch({ type: 'TOKEN_REFRESH', payload: tokens });

    // Notificar a otras pestañas
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
      channel.postMessage({ type: 'TOKEN_REFRESHED', payload: tokens });
      channel.close();
    }

    /* Los tokens nuevos se guardan igual porque son los únicos válidos,
       pero si no corresponden a lo pedido no se debe guardar el perfil */
    if (!tokenCorresponde(response.AccessToken)) {
      throw new Error(`El token emitido no corresponde a ${destino}`);
    }
  }, [refrescarTokenInterno]);

  /**
   * Seleccionar la entidad de la sesión: el backend emite un nuevo par de tokens con los claims
   * `IdRelUsuarioEntidades`, `IdEntidad` e `IdRol`
   */
  const selectEntity = useCallback((idRelUsuarioEntidades: number): Promise<void> => {
    return ejecutarRotacion(() => reemplazarTokensSesion(
      (refreshToken) => SeleccionarEntidadService({ IdRelUsuarioEntidades: idRelUsuarioEntidades, RefreshToken: refreshToken }),
      /* Si el claim no viene se acepta; si viene, debe ser la relación pedida */
      (accessToken) => {
        const idRelToken = obtenerClaimToken(accessToken, CLAIM_ID_REL_USUARIO_ENTIDADES);
        return idRelToken === null || idRelToken === String(idRelUsuarioEntidades);
      },
      'la entidad'
    ));
  }, [ejecutarRotacion, reemplazarTokensSesion]);

  /**
   * Quitar la entidad de la sesión al pasar a Persona Física: el backend emite un nuevo par de tokens
   * sin los claims `IdRelUsuarioEntidades` ni `IdEntidad`.
   * Solo llama al backend si el token actual trae una entidad. Devuelve `true` si se emitieron tokens nuevos
   */
  const selectPersonaFisica = useCallback((): Promise<boolean> => {
    return ejecutarRotacion(async () => {
      if (!tokenTieneEntidad(storage.get(StorageKeys.ACCESS_TOKEN))) return false;

      await reemplazarTokensSesion(
        (refreshToken) => SeleccionarPersonaFisicaService({ RefreshToken: refreshToken }),
        (accessToken) => !tokenTieneEntidad(accessToken),
        'Persona Física'
      );
      return true;
    });
  }, [ejecutarRotacion, reemplazarTokensSesion]);

  /**
   * Escuchar evento de logout disparado desde Http.service
   * Esto evita el parpadeo cuando el token expira
   */
  useEffect(() => {
    const handleAuthLogout = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth:logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  /**
   * Escuchar evento de refresh token desde Http.service
   */
  useEffect(() => {
    const handleRefreshTokenNeeded = async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Error al refrescar el token:', error);
      }
    };

    window.addEventListener('auth:refresh-token-needed', handleRefreshTokenNeeded);

    return () => {
      window.removeEventListener('auth:refresh-token-needed', handleRefreshTokenNeeded);
    };
  }, [refreshToken]); // Dependencia de refreshToken (la función memoizada)

  /**
   * Realizar login
   */
  const login = (loginData: LoginResponse) => {
    try {
      /* Validar seguridad del dispositivo antes de autenticar */
      const deviceSecurity = loginData.deviceSecurity;
      
      /* Bloquear acceso si:
        1. El dispositivo no es conocido (isKnownDevice === false), O
        2. El dispositivo es conocido pero requiere confirmación (isKnownDevice === true && requiresConfirmation === true)
      */
      const shouldBlockAccess = 
        !deviceSecurity.isKnownDevice || 
        (deviceSecurity.isKnownDevice && deviceSecurity.requiresConfirmation);

      if (shouldBlockAccess) {
        console.warn('Acceso bloqueado por seguridad de dispositivo:', {
          isKnownDevice: deviceSecurity.isKnownDevice,
          requiresConfirmation: deviceSecurity.requiresConfirmation,
          deviceInfo: deviceSecurity.deviceInfo
        });

        // Ejecutar logout automáticamente y bloquear acceso
        dispatch({
          type: 'DEVICE_BLOCKED',
          payload: { deviceInfo: deviceSecurity.deviceInfo, email: loginData?.user?.email }
        });
        return;
      }

      // Si pasa la validación de seguridad, proceder con el login normal
      const user: AuthUser = {
        id: loginData.user.id.toString?.(),
        idPerfil: loginData.user.idPerfil,
        fullName: loginData?.user.fullName,
        email: loginData?.user.email,
        imageUrl: loginData?.user.imageUrl,
        matricula: loginData?.user.matricula,
        genero: loginData?.user.genero,
        idSexo: loginData?.user.idSexo,
        permisos: loginData?.permisos,
        identificacion: loginData?.user.identificacion,
        esPrimerInicioSesion: loginData?.user.esPrimerInicioSesion,
        esActivo2FA: loginData?.user.esActivo2FA,
        systemTime: loginData?.systemTime,
      };

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens: loginData.tokens }
      });
    } catch (error) {
      console.error('Error durante el inicio de sesión:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
    }
  };

  /**
   * Función para realizar logout
   */
  const logout = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      /* Llamar al servicio de logout si hay token */
      if (state.refreshToken) {
        await Logout({});
      }
    } catch (error) {
      console.error('Error durante el cierre de sesión:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  /**
   * Verificar si el token ha expirado
   * Buffer reducido a 60 segundos para tokens de 15 minutos
   */
  const isTokenExpired = (): boolean => {
    if (!state.expiresAt) return true;
    
    try {
      const expirationTime = new Date(state.expiresAt).getTime();
      const currentTime = new Date().getTime();
      // Buffer de 60 segundos (tokens de 120 min = 119 min efectivos)
      const bufferTime = 60 * 1000; // 60 segundos en milisegundos
      
      return (expirationTime - currentTime) <= bufferTime;
    } catch {
      return true;
    }
  };

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  const hasPermission = (modulo: string, permiso?: string): boolean => {
    if (!state.user?.permisos || !Array.isArray(state.user.permisos)) {
      // console.warn('hasPermission: No hay permisos disponibles o no es un array');
      return false;
    }

    const result = state.user.permisos.some(p => {
      // Normalizar valores para comparación
      const moduloNormalized = (p.Modulo || '').toString().trim().toLowerCase();
      const permisoNormalized = (p.Permiso || '').toString().trim().toLowerCase();
      const moduloTarget = (modulo || '').toString().trim().toLowerCase();
      const permisoTarget = (permiso || '').toString().trim().toLowerCase();

      const hasModulo = moduloNormalized === moduloTarget;

      // Si no se especifica permiso, solo verificar módulo activo
      if (!permiso || permisoTarget === '') {
        const result = hasModulo && p.Activo;
        // if (process.env.NODE_ENV === 'development' && hasModulo) {
        //   // console.log(`Módulo encontrado: ${p.Modulo}, Activo: ${p.Activo}, Resultado: ${result}`);
        // }
        return result;
      }

      // Verificar módulo, permiso y estado activo
      const result = hasModulo && permisoNormalized === permisoTarget && p.Activo;
      
      // if (process.env.NODE_ENV === 'development' && hasModulo) {
      //   console.log(`Permiso encontrado: ${p.Modulo}.${p.Permiso}, Activo: ${p.Activo}, Match: ${permisoNormalized === permisoTarget}, Resultado: ${result}`);
      // }

      return result;
    });

    // if (process.env.NODE_ENV === 'development') {
    //   if (!result) {
    //     // Mostrar permisos disponibles para debugging
    //     const permisosDisponibles = state.user.permisos
    //       .filter(p => p.Modulo.toLowerCase().includes(modulo.toLowerCase()))
    //       .map(p => ({ modulo: p.Modulo, permiso: p.Permiso, activo: p.Activo }));
    //     console.log('Permisos disponibles para el módulo:', permisosDisponibles);
    //   }
    // }

    return result;
  };

  /**
   * Limpiar el bloqueo de dispositivo
   */
  const clearDeviceBlock = useCallback(() => {
    dispatch({ type: 'CLEAR_DEVICE_BLOCK' });
  }, []);

  const contextValue: AuthContextType = {
    state,
    login,
    logout,
    refreshToken,
    selectEntity,
    selectPersonaFisica,
    isTokenExpired,
    hasPermission,
    clearDeviceBlock,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {state.deviceSecurityBlock?.isBlocked && (
        <DeviceBlocked
          email={state.email || ''}
          deviceInfo={state.deviceSecurityBlock.deviceInfo}
          onClose={clearDeviceBlock}
        />
      )}
    </AuthContext.Provider>
  );
};

/**
 * Hook personalizado para usar el contexto de autenticación
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe utilizarse dentro de (un) AuthProvider');
  }
  return context;
};
